import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
  shareTextAllCleared,
  shareTextChatChunkApplied,
  shareTextDialpadCleared,
  shareTextEntryAppended,
} from './shareTextEvents';

import type { RootState } from 'features/app/store/store';
import type {
  ShareTextState,
  ShareTextValueEntry,
} from 'features/share-text/interfaces';

export const initialShareTextState: ShareTextState = {
  outBoundShareTextValues: [],
};

const shareTextSlice = createSlice({
  name: 'shareText',
  initialState: initialShareTextState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(shareTextEntryAppended, (state, action) => {
      appendEntry(state.outBoundShareTextValues, action.payload);
    });
    builder.addCase(shareTextChatChunkApplied, (state, action) => {
      applyChatChunk(state.outBoundShareTextValues, action.payload);
    });
    builder.addCase(shareTextDialpadCleared, (state) => {
      state.outBoundShareTextValues = state.outBoundShareTextValues.filter(
        (entry) => entry.source !== 'dialpad'
      );
    });
    builder.addCase(shareTextAllCleared, (state) => {
      state.outBoundShareTextValues = [];
    });
  },
});

export const selectShareTextValues = ({ shareText }: RootState) =>
  shareText.outBoundShareTextValues;

export const selectShareTextValuesText = createSelector(
  selectShareTextValues,
  (entries) => entries.map((entry) => entry.value).join('')
);

export const selectShareTextChatText = createSelector(
  selectShareTextValues,
  (entries) =>
    entries
      .filter((entry) => entry.source === 'chat')
      .map((entry) => entry.value)
      .join('')
);

export const selectShareTextDialpadText = createSelector(
  selectShareTextValues,
  (entries) =>
    entries
      .filter((entry) => entry.source === 'dialpad')
      .map((entry) => entry.value)
      .join('')
);

export const shareTextReducer = shareTextSlice.reducer;

const appendEntry = (
  entries: ShareTextValueEntry[],
  entry: ShareTextValueEntry
) => {
  if (!entry.value) {
    return;
  }

  const lastEntry = entries.at(-1);
  if (lastEntry && lastEntry.source === entry.source) {
    lastEntry.value += entry.value;
    return;
  }

  entries.push(entry);
};

const removeLastCharacter = (entries: ShareTextValueEntry[]) => {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry.source !== 'chat') {
      continue;
    }

    entry.value = entry.value.slice(0, -1);
    if (!entry.value.length) {
      entries.splice(i, 1);
    }
    return;
  }
};

const applyChatChunk = (entries: ShareTextValueEntry[], chatChunk: string) => {
  if (!chatChunk) {
    return;
  }

  if (!chatChunk.includes('\b')) {
    appendEntry(entries, {
      source: 'chat',
      value: chatChunk,
    });
    return;
  }

  for (const char of chatChunk) {
    if (char === '\b') {
      removeLastCharacter(entries);
    } else {
      appendEntry(entries, {
        source: 'chat',
        value: char,
      });
    }
  }
};
